import { ColDef, EditableCallbackParams, ValueParserParams } from 'ag-grid-community'
import { currencyFormatter, percentageFormatter } from 'components/AgGrid/utils/formatters'
import type { ApprovalOption } from 'pages/DecisionEditPage/components/ComparisonTabs'
import { ProductLineSummaryTreeNode } from 'ts/interfaces/productLineSummary.interface'
import { createDataPointsValue } from '../../DataPoint/createDataPointsValue'
import DataPointIndicator from '../../DataPoint/DataPointIndicator'
import { renderEditableValue } from '../../shared/EditableCell'
import { validateOgmPercentValue } from '../../validations'

type DataPointsCellProps = {
  rowData?: ProductLineSummaryTreeNode
  showRequestedPrice: boolean
}

const HEADER_CHARACTER_WIDTH_PX = 7
const HEADER_HORIZONTAL_PADDING_PX = 36

const getHeaderMinWidth = (headerName?: string, currentWidth?: number): number => {
  const calculatedHeaderWidth = Math.ceil(
    (headerName?.length ?? 0) * HEADER_CHARACTER_WIDTH_PX + HEADER_HORIZONTAL_PADDING_PX
  )

  return Math.max(calculatedHeaderWidth, Number(currentWidth ?? 0))
}

const DataPointsCell = ({ rowData, showRequestedPrice }: DataPointsCellProps) => {
  if (!rowData || rowData.id === 'totals') {
    return null
  }

  const value = createDataPointsValue({
    baseline: rowData.dataPoints,
    ogmOrMargin: rowData.ogmPercent,
    requestedValue: rowData.requestedOgmPercent ?? rowData.ogmPercent,
    isRequestedPriceActive: showRequestedPrice,
  })

  if (!value) {
    return null
  }

  return <DataPointIndicator value={value} />
}

const withMinWidth = <T,>(col: ColDef<T>): ColDef<T> => ({
  ...col,
  minWidth: getHeaderMinWidth(col.headerName, col.width),
  wrapHeaderText: true,
  autoHeaderHeight: true,
})

const baseProductLineColDefs: ColDef<ProductLineSummaryTreeNode>[] = [
  {
    headerName: 'PL Description',
    field: 'description',
    width: 230,
  },
  {
    headerName: 'Net Revenue',
    field: 'netRevenue',
    width: 110,
    valueFormatter: currencyFormatter,
    type: 'numericColumn',
    aggFunc: null,
  },
  {
    headerName: 'Data Points',
    field: 'dataPoints',
    width: 230,
  },
  {
    headerName: 'Total Discount %',
    field: 'totalDiscountPercentUpdated',
    width: 130,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
  },
  {
    headerName: 'OGM %',
    field: 'ogmPercent',
    width: 75,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    editable: (params: EditableCallbackParams<ProductLineSummaryTreeNode>) =>
      !params.node.group && !params.node.rowPinned && !params.context?.isUSDSelected,
    cellEditor: 'agNumberCellEditor',
    valueParser: (params: ValueParserParams) => Number(params.newValue),
    valueSetter: (params) => validateOgmPercentValue(params),
    cellRenderer: renderEditableValue,
    cellClass: (params) =>
      params.node.group || params.node.rowPinned || params.context?.isUSDSelected
        ? ''
        : 'editable-bordered-cell',
  },
  {
    headerName: 'Expert Discount %',
    field: 'expertDiscountPercentage',
    width: 130,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
  {
    headerName: 'Typical Discount %',
    field: 'typicalDiscountPercentage',
    width: 130,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
  {
    headerName: 'Floor Discount %',
    field: 'floorDiscountPercentage',
    width: 130,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
  {
    colId: 'requested-total-discount-percent',
    headerName: 'Total Discount %',
    field: 'totalDiscountPercent',
    width: 130,
    valueGetter: (params) => (params.node?.rowPinned ? null : params.data?.totalDiscountPercent),
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
  {
    colId: 'requested-ogm-percent',
    headerName: 'OGM %',
    field: 'requestedOgmPercent',
    width: 75,
    valueGetter: (params) => (params.node?.rowPinned ? null : params.data?.requestedOgmPercent),
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
]

export const productLineColDefs: ColDef<ProductLineSummaryTreeNode>[] =
  baseProductLineColDefs.map(withMinWidth)

const buildApprovalColDefs = (
  approvalOption: ApprovalOption
): ColDef<ProductLineSummaryTreeNode>[] => {
  const { id } = approvalOption
  return [
    withMinWidth({
      colId: `approval-${id}-total-discount-percent`,
      headerName: `Total Discount %`,
      width: 130,
      valueGetter: (params) =>
        params.node?.rowPinned
          ? null
          : (params.data?.approvalData?.[id]?.totalDiscountPercentage ?? null),
      valueFormatter: percentageFormatter,
      type: 'numericColumn',
      aggFunc: null,
      hide: true,
    }),
    withMinWidth({
      colId: `approval-${id}-ogm-percent`,
      headerName: `OGM %`,
      width: 75,
      valueGetter: (params) =>
        params.node?.rowPinned ? null : (params.data?.approvalData?.[id]?.ogmPercent ?? null),
      valueFormatter: percentageFormatter,
      type: 'numericColumn',
      aggFunc: null,
      hide: true,
    }),
  ]
}

export const getProductLineColDefs = (
  showRequestedPrice: boolean,
  approvalOptions: ApprovalOption[] = []
): ColDef<ProductLineSummaryTreeNode>[] => {
  const approvalColDefs = approvalOptions.flatMap(buildApprovalColDefs)
  const allCols = [...productLineColDefs, ...approvalColDefs]

  return allCols.map((col) => {
    if (col.field === 'dataPoints') {
      return {
        ...col,
        cellRenderer: (params: { data?: ProductLineSummaryTreeNode }) => (
          <DataPointsCell rowData={params.data} showRequestedPrice={showRequestedPrice} />
        ),
      }
    }

    if ((col.colId || '').startsWith('requested-')) {
      return { ...col, hide: !showRequestedPrice }
    }

    return col
  })
}
